import get from 'lodash/get';

import type { PipeMap } from 'Types/pipes.types';
import type { CachedPipe, Flow, ResolverMap } from '../types/flows.types';
import type { DownstreamMap, LookupMap } from 'Types/common.types';

const flowWorker = new Worker(new URL('./flows.worker', import.meta.url), {
  type: 'module',
});

const getFlows = () => {
  let previousPipes: CachedPipe[] = [];
  let cachedFlows: Flow[] = [];
  let resolvers: ResolverMap = {};
  let inFlightId: string | null = null;

  flowWorker.onmessage = (event: MessageEvent) => {
    const flows = event.data.flows as Flow[];
    cachedFlows = flows;
    inFlightId = null;
    // Resolve all waiting callers (concurrent calls piggyback on the same request).
    const pending = resolvers;
    resolvers = {};
    for (const resolve of Object.values(pending)) {
      resolve(flows);
    }
  };

  const getFlows = (pipes: PipeMap, downstreams: DownstreamMap, lookups: LookupMap) => {
    return new Promise<Flow[]>((resolve) => {
      if (!isUpdated(pipes)) {
        // If a request is already in-flight for these same pipes, piggyback on it.
        if (inFlightId !== null) {
          const id = `${Date.now()}-${Math.random()}`;
          resolvers[id] = resolve;
          return;
        }
        return resolve(cachedFlows);
      }

      const id = `${Date.now()}-${Math.random()}`;
      savePipes(pipes);
      inFlightId = id;
      resolvers[id] = resolve;
      flowWorker.postMessage({ pipes, downstreams, lookups, id });
    });
  };

  const isUpdated = (pipesAfter: PipeMap) => {
    // different lengths, definitely different
    let pipesAfterList = Object.values(pipesAfter);
    let pipesBefore = previousPipes;
    if (pipesBefore.length !== pipesAfterList.length) {
      return true;
    }
    const length = pipesBefore.length;

    let same = true;
    for (let i = 0; i < length; i++) {
      const pipeAfterId = pipesAfterList[i]._id;
      const pipeAfterTs = get(pipesAfterList[i], 'config.audit.last_modified_ts');
      const pipeBefore = pipesBefore.find((p) => p._id === pipeAfterId);
      if (!pipeBefore) {
        return true;
      }
      const pipeBeforeTs = pipeBefore.ts;
      if (pipeBeforeTs !== pipeAfterTs) {
        same = false;

        break;
      }
    }

    return !same;
  };

  const savePipes = (pipes: PipeMap) => {
    let pipesList = Object.values(pipes);
    previousPipes = pipesList.map((p) => ({
      _id: p._id,
      ts: get(p, 'config.audit.last_modified_ts'),
    }));
  };

  const clearCache = () => {
    previousPipes = [];
    cachedFlows = [];
  };

  return { get: getFlows, clear: clearCache };
};

const Flows = getFlows();

export { Flows };
